import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import BoxCard from "shared/components/box/BoxCard.js";
import ButtonMflClub from "shared/components/buttons/ButtonMflClub.js";
import ButtonSelect from "shared/components/buttons/ButtonSelect.js";
import LoadingSquare from "shared/components/loading/LoadingSquare.js";
import MiscClubPerformance from "shared/components/misc/MiscClubPerformance";
import MiscCompetitionRanking from "shared/components/misc/MiscCompetitionRanking";
import MiscDepthField from "shared/components/misc/MiscDepthField";
import { copyTextToClipboard } from "shared/utils/clipboard.js";
import {
  getFullCompetitionsLimited,
  getMatches,
  getOverallVsGdRates,
  getClubs,
} from "shared/services/api-assistant.js";
import MiscClubStats from "shared/components/misc/MiscClubStats.js";

const PageClub = (props) => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedView = searchParams.get("view");
  const view = ["projection", "performance", "squad"].includes(requestedView) ? requestedView : "projection";
  const setView = (value) => {
    setSearchParams((params) => {
      const nextParams = new URLSearchParams(params);
      nextParams.set("view", value);
      return nextParams;
    });
  };

  const [club, setClub] = useState(null);
  const [competitions, setCompetitions] = useState(null);
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [matches, setMatches] = useState(null);
  const [types, setTypes] = useState(["LEAGUE", "CUP"]);
  const [overallVsGdRates, setOverallVsGdRates] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch club data
  useEffect(() => {
    setIsLoading(true);
    const num = parseInt(id) || 0;
    getClubs({
      handleSuccess: (v) => {
        setClub(v.data.getClubs[0]);
        setIsLoading(false);
      },
      params: {
        id: num,
      },
    });
  }, [id]);

  const fetchClubStandings = () => {
    if (!club) return;
    getFullCompetitionsLimited({
      handleSuccess: (v) => setCompetitions(v.data.getCompetitions),
      handleError: (e) => console.log(`Error fetching standings for club ID ${club.id}:`, e),
      params: { participants: [club.id] },
    });
  };

  const fetchOverallVsGdRates = () => {
    getOverallVsGdRates({
      handleSuccess: (d) => setOverallVsGdRates(d.data.getOverallVsGdRates),
      handleError: console.log,
      params: { engine: "15" },
    });
  };

  const fetchMatches = () => {
    if (!club || !selectedCompetition) return;
    setMatches(null);
    getMatches({
      handleSuccess: (d) => setMatches(d.data.getMatches),
      handleError: console.log,
      params: {
        limit: 20,
        clubs: [club.id],
        statuses: ["LIVE", "ENDED"],
        types: types,
        minDate: selectedCompetition?.startingDate?.slice(0, 10),
        maxDate: endDate?.slice(0, 10),
      },
    });
  };

  useEffect(() => {
    if (club) {
      fetchClubStandings();
      fetchOverallVsGdRates();
    }
  }, [club]);

  useEffect(() => {
    if (club && selectedCompetition) {
      fetchMatches();
    }
  }, [club, selectedCompetition, types]);

  const getCompetitionBlocks = () => {
    if (!competitions || competitions.length === 0) return [];
    const maxSeason = Math.max(...competitions.map((c) => c.season));
    const filtered = competitions.filter((c) => c.season === maxSeason);
    const sorted = filtered.sort((a, b) => (a.type === "LEAGUE" ? -1 : b.type === "LEAGUE" ? 1 : 0));
    return sorted.map((competition) => (
      <MiscCompetitionRanking key={competition.id} competition={competition} clubToHighlight={[club.id]} />
    ));
  };

  if (!club) {
    return <LoadingSquare />;
  }

  return (
    <div id="PageClub" className="d-flex flex-column w-100 h-100">
      <nav className="MenuTop d-flex navbar">
        <h3 className="my-2 ps-5">
          <i className="bi bi-buildings-fill me-2"></i> {club.name}
        </h3>
      </nav>
      <div className="container container-lg max-width-lg w-100 px-1 px-md-4 py-4">
        <div className="d-flex flex-column flex-sm-row m-1 mb-2">
          <div className="d-flex flex-column flex-basis-200">
            <div className="d-flex justify-content-center mb-3">
              <img
                src={`https://d13e14gtps4iwl.cloudfront.net/u/clubs/${club.id}/logo.webp`}
                alt={`${club.name} logo`}
                style={{ width: "70%", maxWidth: "250px" }}
              />
            </div>

            <div className="px-2 mb-3 d-flex flex-row justify-content-center align-items-center">
              <div className="me-1">
                <ButtonMflClub clubId={club.id} />
              </div>
              <button className="btn btn-link btn-sm" onClick={() => copyTextToClipboard(window.location.href)}>
                <i className="bi bi-share-fill" />
              </button>
            </div>
          </div>

          <div className="d-flex flex-column flex-fill">
            <div className="d-flex flex-column-reverse flex-column flex-md-row align-items-end justify-content-end px-2">
              <div className="mb-1">
                <ButtonSelect
                  selected={view}
                  onChange={(v) => setView(v)}
                  buttons={{
                    projection: "Ranking and projection",
                    performance: "Performance",
                    squad: "Squad depth",
                  }}
                />
              </div>
            </div>

            {view === "projection" && <MiscClubStats club={club} />}

            {view === "projection" && (
              <BoxCard
                className="mb-2"
                title={"Ranking and projection"}
                content={
                  competitions ? (
                    <div className="d-flex flex-column flex-fill">{getCompetitionBlocks()}</div>
                  ) : (
                    <div style={{ height: 300 }} className="w-100">
                      <LoadingSquare height={300} />
                    </div>
                  )
                }
              />
            )}

            {view === "performance" && <MiscClubPerformance clubId={club.id} clubType={club.type} />}

            {view === "squad" && (
              <BoxCard className="h-md-100 p-0" noPadding={true} content={<MiscDepthField clubId={club.id} />} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageClub;
